import React, { FC, ChangeEvent, useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  MenuItem,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Autocomplete,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  getItemsByCategory,
  saveStockTransaction,
} from "../../Services/inventoryService";
import axios from "axios";
import { useSnackbar } from "notistack";
import { LBL_CANCEL, LBL_SAVE } from "../../Constants/LabelConstants";
import {
  MSG_CATEGORY_REQUIRED,
  MSG_DATE_REQUIRED,
  MSG_ITEM_NAME_REQUIRED,
  MSG_SELL_TYPE_REQUIRED,
  MSG_STOCK_ADDED,
  MSG_STOCK_ERROR,
  MSG_STOCK_REQUIRED,
} from "../../Constants/MessageConstant";

interface StockPopupProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  categoryOptions: { id: string; label: string }[];
}
//  itemOptions: { label: string; value: any; itemName: string; itemId: string }[];

interface FormData {
  category: string;
  itemId: string;
  itemName: string;
  sellType: string;
  stock: string;
  date: string;
}
interface FormErrors {
  [key: string]: string;
}

const items = ["ACC", "Ultratech", "JK Lakshmi"];
const sellTypes = ["In", "Out"];

const StockPopup: FC<StockPopupProps> = ({
  open,
  setOpen,
  categoryOptions,
}) => {
  const [formData, setFormData] = useState<FormData>({
    category: "",
    itemId: "",
    itemName: "",
    sellType: "",
    stock: "",
    date: "",
  });

  const { enqueueSnackbar } = useSnackbar();

  const [dateFrom, setDateFrom] = useState<Dayjs | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});
  const [itemOptions, setItemOptions] = useState<
    { label: string; value: any; itemName: string; itemId: string }[]
  >([]);
  const [saving, setSaving] = useState(false);
  const sellTypeMap: Record<string, "StockIn" | "StockOut"> = {
    In: "StockIn",
    Out: "StockOut",
  };
  const orgId = localStorage.getItem("orgId") || "";

  useEffect(() => {
    const fetchItems = async () => {
      if (!formData.category) {
        setItemOptions([]);
        return;
      }
      
      
      try {
        const items = await getItemsByCategory(orgId, formData.category);
        const options = items.map((item: any) => {
          const shortDesc = item.description
            ? " - " +
              item.description.split(" ").slice(0, 5).join(" ") +
              (item.description.split(" ").length > 5 ? "..." : "")
            : "";
          return {
            label: `${item.itemName}${shortDesc}`,
            value: item.itemId,
            itemName: item.itemName,
            itemId: item.itemId,
          };
        });

        setItemOptions(options);
        setFormData((prev) => ({
          ...prev,
          itemName: "",
          itemId: "",
        }));
      } catch (err) {
        console.error("Error fetching items:", err);
        setItemOptions([]);
      }
    };

    fetchItems();
  }, [formData.category]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (value.trim() !== "") {
      setErrors((prev) => ({ ...prev, [name]: "" }));
      setErrors((prev) => ({ ...prev, sellType: "" }));
    }
  };
  const handleDateChange = (newValue: Dayjs | null) => {
    setDateFrom(newValue);
    if (newValue) {
      const formatted = dayjs(newValue).format("YYYY-MM-DD");
      setFormData((prev) => ({ ...prev, date: formatted }));
      setErrors((prev) => ({ ...prev, date: "" }));
    } else {
      setFormData((prev) => ({ ...prev, date: "" }));
    }
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};
    if (!formData.category) newErrors.category = MSG_CATEGORY_REQUIRED;
    if (!formData.itemName) newErrors.itemName = MSG_ITEM_NAME_REQUIRED;
    if (!formData.sellType) newErrors.sellType = MSG_SELL_TYPE_REQUIRED;
    if (!formData.stock) newErrors.stock = MSG_STOCK_REQUIRED;
    if (!formData.date) newErrors.date = MSG_DATE_REQUIRED;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const initialFormData = {
    itemId: "",
    category: "",
    itemName: "",
    stock: "",
    sellType: "",
    date: "",
  };
  useEffect(() => {
    if (open) {
      setFormData({
        category: "",
        itemId: "",
        itemName: "",
        sellType: "",
        stock: "",
        date: "",
      });
      setDateFrom(null); // ✅ clears DatePicker
      setErrors({});
    }
  }, [open]);
  const handleClose = () => {
    setFormData(initialFormData);
    setOpen(false);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    const payload = {
      orgId: orgId,
      itemId: formData.itemId,
      categoryId: formData.category,
      categoryName:
        categoryOptions.find((c) => c.id === formData.category)?.label || "",
      itemName: formData.itemName,
      quantity: Number(formData.stock),
      saleType: sellTypeMap[formData.sellType],
      createdBy: "admin", // 🔁 replace with actual user ID
      createdByIp: window.location.hostname,
      date: formData.date,
    };
    try {
      setSaving(true);
      const response = await saveStockTransaction(payload);
      enqueueSnackbar(MSG_STOCK_ADDED, { variant: "success" });

      setFormData(initialFormData);
      setErrors({});
      setOpen(false);
    } catch (error) {
      let message = MSG_STOCK_ERROR;

      if (axios.isAxiosError(error)) {
        if (error.response?.data?.message) {
          message = error.response.data.message;
        } else if (error.message) {
          message = error.message; // e.g. Network Error
        }
      } else if (error instanceof Error) {
        message = error.message;
      } else {
        message = String(error); // fallback for unknown error shapes
      }
      enqueueSnackbar(message, { variant: "error" });
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        style: {
          maxHeight: "80vh",
          marginTop: "20vh",
          overflowY: "auto",
          overflow: "visible",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textDecoration: "underline",
          fontWeight: "bold",
        }}
      >
        Add Stock
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          //   maxHeight: "60vh",
          overflowY: "auto",
        }}
      >
        <Grid container spacing={2} mt={1}>
          <Grid item xs={6}>
            <Autocomplete
              fullWidth
              options={categoryOptions}
              getOptionLabel={(option) => option.label || ""}
              value={
                categoryOptions.find(
                  (option) => option.id === formData.category
                ) || null
              }
              onChange={(event, newValue) => {
                setFormData({ ...formData, category: newValue?.id || "" });
                setErrors((prev) => ({ ...prev, category: "" }));
              }}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Category"
                  error={!!errors.category}
                  helperText={errors.category}
                />
              )}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              select
              label="Sell Type"
              name="sellType"
              value={formData.sellType}
              onChange={handleChange}
              fullWidth
              error={!!errors.sellType}
              helperText={errors.sellType}
            >
              {sellTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              select
              label="Item Name"
              name="itemName"
              value={formData.itemName}
              onChange={(e) => {
                const selectedItem = itemOptions.find(
                  (i) => i.itemName === e.target.value
                );
                setFormData((prev) => ({
                  ...prev,
                  itemName: selectedItem?.itemName || "",
                  itemId: selectedItem?.itemId || "",
                }));

                setErrors((prev) => ({ ...prev, itemName: "" }));
              }}
              fullWidth
              error={!!errors.itemName}
              helperText={errors.itemName}
            >
              {itemOptions.map((item) => (
                <MenuItem key={item.itemId} value={item.itemName}>
                  {item.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={6}>
            <DatePicker
              label="Date"
              value={dateFrom}
              onChange={handleDateChange}
              disableFuture
              minDate={dayjs(new Date(0))}
              slotProps={{
                popper: {
                  placement: "top-start", // 📌 opens calendar above
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, 10], // 📌 space between input & calendar
                      },
                    },
                  ],
                },
                textField: {
                  fullWidth: true,
                  error: !!errors.date,
                  helperText: errors.date,
                },
              }}
            />
          </Grid>

          <Grid item xs={6}>
            <TextField
              type="text"
              label="Stock"
              name="stock"
              value={formData.stock}
              onChange={(e) => {
                const value = e.target.value;

                const isValid =
                  /^(\d{0,10})$/.test(value) || // Whole numbers up to 10 digits
                  (/^\d{1,10}(\.\d{0,2})?$/.test(value) &&
                    value.replace(/\D/g, "").length <= 10);

                if (isValid) {
                  handleChange(e);
                }
              }}
              onKeyDown={(e) => {
                if (["+", "-", "e", "E"].includes(e.key)) {
                  e.preventDefault();
                }
              }}
              fullWidth
              error={!!errors.stock}
              helperText={errors.stock}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={() => setOpen(false)}>{LBL_CANCEL}</Button>
        {/* <Button variant="contained" onClick={handleSave}>
          Save
        </Button> */}
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {/* {saving ? "Saving..." : "Save"} */}
          {saving ? <CircularProgress size={20} color="inherit" /> : LBL_SAVE}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StockPopup;
